(() => {
  const initIndustryHeroes = (root = document) => {
    root.querySelectorAll("[data-sa-industry-hero]").forEach((heroRegion) => {
      if (heroRegion.dataset.saReady === "true") return;

      const banners = heroRegion.querySelector("[data-sa-home-banners]");
      const tabs = Array.from(heroRegion.querySelectorAll("[data-sa-mega-tab]"));
      const panels = Array.from(heroRegion.querySelectorAll("[data-sa-mega-panel]"));
      if (!banners || !tabs.length || !panels.length) return;

      heroRegion.dataset.saReady = "true";
      let activeTab = null;
      let isLocked = false;
      let openTimer = null;
      let closeTimer = null;

      const isDesktop = () => window.matchMedia("(min-width: 761px)").matches;

      const clearTimers = () => {
        window.clearTimeout(openTimer);
        window.clearTimeout(closeTimer);
      };

      const setBannersVisible = (isVisible) => {
        banners.hidden = !isVisible;
        banners.classList.toggle("is-active", isVisible);
      };

      const closeMenu = ({ focusTab = false, force = true } = {}) => {
        if (!force && isLocked) return;
        const tabToFocus = activeTab;
        activeTab = null;
        isLocked = false;
        clearTimers();

        tabs.forEach((tab) => {
          tab.classList.remove("is-active");
          tab.setAttribute("aria-expanded", "false");
        });

        panels.forEach((panel) => {
          panel.classList.remove("is-active");
          panel.hidden = true;
        });

        setBannersVisible(true);

        if (focusTab && tabToFocus) {
          tabToFocus.focus();
        }
      };

      const openMenu = (tab, { lock = false } = {}) => {
        const panelId = tab.getAttribute("aria-controls");
        clearTimers();
        activeTab = tab;
        isLocked = lock;
        setBannersVisible(false);

        tabs.forEach((candidate) => {
          const isActive = candidate.getAttribute("aria-controls") === panelId;
          candidate.classList.toggle("is-active", isActive);
          candidate.setAttribute("aria-expanded", isActive ? "true" : "false");
        });

        panels.forEach((panel) => {
          const isActive = panel.id === panelId;
          panel.classList.toggle("is-active", isActive);
          panel.hidden = !isActive;
        });
      };

      const scheduleOpen = (tab) => {
        if (!isDesktop() || isLocked) return;
        window.clearTimeout(closeTimer);
        window.clearTimeout(openTimer);
        openTimer = window.setTimeout(() => openMenu(tab), 180);
      };

      const scheduleClose = () => {
        if (!isDesktop() || isLocked) return;
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => closeMenu({ force: false }), 220);
      };

      const moveFocus = (current, direction) => {
        const index = tabs.indexOf(current);
        if (index === -1) return;
        const nextIndex = (index + direction + tabs.length) % tabs.length;
        tabs[nextIndex].focus();
      };

      tabs.forEach((tab) => {
        ["mouseenter", "pointerenter", "mouseover", "mousemove"].forEach((eventName) => {
          tab.addEventListener(eventName, () => scheduleOpen(tab));
        });
        tab.addEventListener("focus", () => {
          if (isDesktop()) openMenu(tab);
        });

        tab.addEventListener("click", () => {
          if (tab.getAttribute("aria-expanded") === "true" && isLocked) {
            closeMenu();
          } else {
            openMenu(tab, { lock: true });
          }
        });

        tab.addEventListener("keydown", (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (tab.getAttribute("aria-expanded") === "true" && isLocked) {
              closeMenu({ focusTab: true });
            } else {
              openMenu(tab, { lock: true });
            }
          } else if (event.key === "ArrowDown" || event.key === "ArrowRight") {
            event.preventDefault();
            moveFocus(tab, 1);
          } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
            event.preventDefault();
            moveFocus(tab, -1);
          } else if (event.key === "Escape") {
            event.preventDefault();
            closeMenu({ focusTab: true });
          }
        });
      });

      ["mouseenter", "pointerenter", "mouseover"].forEach((eventName) => {
        heroRegion.addEventListener(eventName, () => {
          window.clearTimeout(closeTimer);
        });
      });

      heroRegion.addEventListener("mouseenter", () => {
        window.clearTimeout(closeTimer);
      });

      heroRegion.addEventListener("mouseleave", () => scheduleClose());

      panels.forEach((panel) => {
        panel.querySelectorAll("a").forEach((link) => {
          link.addEventListener("click", () => closeMenu());
        });
      });

      heroRegion.querySelectorAll("[data-sa-mega-close]").forEach((button) => {
        button.addEventListener("click", () => closeMenu({ focusTab: true }));
      });

      document.addEventListener("click", (event) => {
        if (activeTab && !heroRegion.contains(event.target)) {
          closeMenu();
        }
      });

      document.addEventListener("mousemove", (event) => {
        if (!activeTab || isLocked || !isDesktop()) return;
        if (heroRegion.contains(event.target)) {
          window.clearTimeout(closeTimer);
        } else {
          scheduleClose();
        }
      });

      document.addEventListener("keydown", (event) => {
        if (activeTab && event.key === "Escape") {
          closeMenu({ focusTab: true });
        }
      });

      closeMenu();
    });
  };

  const initBrandShowcases = (root = document) => {
    root.querySelectorAll("[data-sa-brand-showcase]").forEach((showcase) => {
      if (showcase.dataset.saBrandReady === "true") return;

      const tabs = Array.from(showcase.querySelectorAll("[data-sa-brand-tab]"));
      const panels = Array.from(showcase.querySelectorAll("[data-sa-brand-panel]"));
      if (!tabs.length || !panels.length) return;

      showcase.dataset.saBrandReady = "true";

      const activateTab = (tab, { focus = false } = {}) => {
        const panelId = tab.getAttribute("aria-controls");

        tabs.forEach((candidate) => {
          const isActive = candidate === tab;
          candidate.classList.toggle("is-active", isActive);
          candidate.setAttribute("aria-selected", isActive ? "true" : "false");
          candidate.tabIndex = isActive ? 0 : -1;
        });

        panels.forEach((panel) => {
          const isActive = panel.id === panelId;
          panel.classList.toggle("is-active", isActive);
          panel.hidden = !isActive;
        });

        if (focus) tab.focus();
      };

      const moveTab = (currentTab, targetIndex) => {
        const nextTab = tabs[(targetIndex + tabs.length) % tabs.length];
        if (nextTab) activateTab(nextTab, { focus: true });
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener("click", () => activateTab(tab));

        tab.addEventListener("keydown", (event) => {
          if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            event.preventDefault();
            moveTab(tab, index + 1);
          } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            event.preventDefault();
            moveTab(tab, index - 1);
          } else if (event.key === "Home") {
            event.preventDefault();
            moveTab(tab, 0);
          } else if (event.key === "End") {
            event.preventDefault();
            moveTab(tab, tabs.length - 1);
          }
        });
      });

      const selectedTab = tabs.find((tab) => tab.getAttribute("aria-selected") === "true") || tabs[0];
      activateTab(selectedTab);
    });
  };

  const initProductTabs = (root = document) => {
    root.querySelectorAll("[data-sa-product-tabs]").forEach((tabsRegion) => {
      if (tabsRegion.dataset.saProductReady === "true") return;

      const tabs = Array.from(tabsRegion.querySelectorAll("[data-sa-product-tab]"));
      const panels = Array.from(tabsRegion.querySelectorAll("[data-sa-product-panel]"));
      const viewAllLink = tabsRegion.querySelector("[data-sa-product-view-all]");
      if (!tabs.length || !panels.length) return;

      tabsRegion.dataset.saProductReady = "true";

      const activateTab = (tab, { focus = false } = {}) => {
        const panelId = tab.getAttribute("aria-controls");

        tabs.forEach((candidate) => {
          const isActive = candidate === tab;
          candidate.classList.toggle("is-active", isActive);
          candidate.setAttribute("aria-selected", isActive ? "true" : "false");
          candidate.tabIndex = isActive ? 0 : -1;
        });

        panels.forEach((panel) => {
          const isActive = panel.id === panelId;
          panel.classList.toggle("is-active", isActive);
          panel.hidden = !isActive;

          if (isActive) {
            const track = panel.querySelector("[data-sa-carousel-track]");
            if (track) track.scrollTo({ left: 0, behavior: "auto" });
          }
        });

        if (viewAllLink && tab.dataset.saProductUrl) {
          viewAllLink.href = tab.dataset.saProductUrl;
        }

        if (focus) tab.focus();
      };

      const moveTab = (targetIndex) => {
        const nextTab = tabs[(targetIndex + tabs.length) % tabs.length];
        if (nextTab) activateTab(nextTab, { focus: true });
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener("click", () => activateTab(tab));

        tab.addEventListener("keydown", (event) => {
          if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            event.preventDefault();
            moveTab(index + 1);
          } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            event.preventDefault();
            moveTab(index - 1);
          } else if (event.key === "Home") {
            event.preventDefault();
            moveTab(0);
          } else if (event.key === "End") {
            event.preventDefault();
            moveTab(tabs.length - 1);
          }
        });
      });

      const selectedTab = tabs.find((tab) => tab.getAttribute("aria-selected") === "true") || tabs[0];
      activateTab(selectedTab);
    });
  };

  const initProductCarousels = (root = document) => {
    root.querySelectorAll("[data-sa-product-carousel]").forEach((carousel) => {
      if (carousel.dataset.saCarouselReady === "true") return;

      const track = carousel.querySelector("[data-sa-carousel-track]");
      const prev = carousel.querySelector("[data-sa-carousel-prev]");
      const next = carousel.querySelector("[data-sa-carousel-next]");
      if (!track || !prev || !next) return;

      carousel.dataset.saCarouselReady = "true";

      const scrollByPage = (direction) => {
        const amount = Math.max(220, track.clientWidth * 0.86);
        track.scrollBy({ left: amount * direction, behavior: "smooth" });
      };

      prev.addEventListener("click", () => scrollByPage(-1));
      next.addEventListener("click", () => scrollByPage(1));
    });
  };

  const initSaHomepage = (root = document) => {
    initIndustryHeroes(root);
    initBrandShowcases(root);
    initProductTabs(root);
    initProductCarousels(root);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initSaHomepage());
  } else {
    initSaHomepage();
  }

  document.addEventListener("shopify:section:load", (event) => initSaHomepage(event.target));
})();
